import { useEffect, useState } from "react"
import { FiPlus } from "react-icons/fi"
import PriorityForm from "./PriorityForm"
import SavedPriorityBox from "./SavedPriorityBox"
import { useResetListener } from "../../hooks/useResetListener"

export default function AddPriorityBox({
  onChange,
  initialData,
  disabled = false
}: {
  onChange?: (list: any[]) => void
  initialData?: any[] | null
  disabled?: boolean
}) {
  const [priorities, setPriorities] = useState<any[]>(initialData || [])
  const [isFormOpen, setIsFormOpen] = useState(false)
  const [editingIndex, setEditingIndex] = useState<number | null>(null)

  useEffect(() => {
    if (initialData) {
      setPriorities(initialData)
    }
  }, [initialData])

  const emitPrioritiesChange = (list: any[]) => {
    // Keep isDeleted markers on already-persisted entries so
    // savePatentDraftService can sync deletions, but drop them for
    // entries that were added and removed again before ever being saved.
    const cleaned = list.filter((priority) => !(priority.isDeleted && !priority._id))
    onChange?.(cleaned)
  }

  useResetListener(() => {
    setPriorities([])
    setEditingIndex(null)
    setIsFormOpen(false)
    onChange?.([])
  })

  const handleSave = (formData: any) => {
    setPriorities((prev) => {
      const updated =
        editingIndex !== null
          ? prev.map((priority, index) =>
              index === editingIndex ? { ...priority, ...formData } : priority
            )
          : [...prev, formData]

      emitPrioritiesChange(updated)
      return updated
    })

    setIsFormOpen(false)
    setEditingIndex(null)
  }

  const handleDelete = (index: number) => {
    setPriorities((prev) => {
      const target = prev[index]
      const updated = target._id
        ? prev.map((priority, i) =>
            i === index ? { ...priority, isDeleted: true } : priority
          )
        : prev.filter((_, i) => i !== index)

      emitPrioritiesChange(updated)
      return updated
    })
  }

  return (
    <div className="flex flex-col gap-6">
      {priorities.length > 0 && (
        <div className="flex flex-col gap-3">
          {priorities.map(
            (priority, index) =>
              !priority.isDeleted && (
                <SavedPriorityBox
                  key={priority._id || index}
                  priority={priority}
                  onEdit={() => {
                    if (disabled) return
                    setEditingIndex(index)
                    setIsFormOpen(true)
                  }}
                  onDelete={() => {
                    if (!disabled) handleDelete(index)
                  }}
                />
              )
          )}
        </div>
      )}

      <button
        type="button"
        disabled={disabled}
        onClick={() => {
          if (!disabled) {
            setEditingIndex(null)
            setIsFormOpen(true)
          }
        }}
        className="flex w-full flex-col items-center justify-center rounded-xl border-[1.5px] border-dashed border-gray-300 bg-gray-50/60 p-5 text-dantzBlue transition-colors hover:border-dantzBlue hover:bg-blue-50/50 disabled:cursor-not-allowed disabled:opacity-50"
      >
        <FiPlus size={20} />
        <span className="mt-1 text-sm font-medium">Add priority claim</span>
        <span className="mt-0.5 text-[11px] text-gray-400">
          Claim priority from an earlier filing of the same invention
        </span>
      </button>

      {isFormOpen && (
        <div className="border border-gray-200 rounded-2xl bg-white p-6 shadow-sm max-w-full space-y-6">
          <PriorityForm
            onCancel={() => setIsFormOpen(false)}
            onSave={handleSave}
            initialValues={editingIndex !== null ? priorities[editingIndex] : undefined}
          />
        </div>
      )}
    </div>
  )
}
